/**
 * Shared hook for AUI chart components.
 *
 * Extracts the ECharts option from an AUI node's props, supporting both
 * inline `option` and VFS-loaded `optionUrl`. Returns the parsed option
 * plus loading/error state for the URL path.
 */

import { useMemo } from 'react';
import { useVfsContent, type VfsContentState } from '@/hooks/useVfsContent';
import type { AuiNode } from '@/types/canvases';
import type { EChartsOption } from 'echarts';

export interface ChartOptionState {
  option: EChartsOption | null;
  height: string | number;
  loading: boolean;
  error: string | null;
}

export function useChartOption(node: AuiNode): ChartOptionState {
  const props = node.component.props ?? {};
  const inlineOption = (props.option as EChartsOption) ?? null;
  const optionUrl = props.optionUrl as string | undefined;
  const height = (props.height as string | number) ?? 400;

  const fetched: VfsContentState = useVfsContent(optionUrl);

  const option = useMemo<EChartsOption | null>(() => {
    if (!optionUrl) return inlineOption;
    if (!fetched.data) return null;
    try {
      return JSON.parse(fetched.data) as EChartsOption;
    } catch {
      return null;
    }
  }, [optionUrl, fetched.data, inlineOption]);

  const error = useMemo<string | null>(() => {
    if (fetched.error) return fetched.error.message;
    if (!optionUrl || !fetched.data) return null;
    try {
      JSON.parse(fetched.data);
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'Invalid JSON';
    }
  }, [optionUrl, fetched.data, fetched.error]);

  return {
    option,
    height,
    loading: !!optionUrl && fetched.loading,
    error,
  };
}
